import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  alpha,
  Card,
  CardContent,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  School as SchoolIcon,
  QuestionAnswer as QuizIcon,
  Timer as DurationIcon,
  Groups as GroupsIcon,
  CheckCircle as CompletionIcon,
  TrendingUp as PerformanceIcon,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ComposedChart,
  RadialBarChart,
  RadialBar,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { Group } from '@visx/group';
import { Cluster, hierarchy } from '@visx/hierarchy';
import { LinkVertical } from '@visx/shape';
import { LinearGradient } from '@visx/gradient';
import { scaleLinear } from '@visx/scale';

const colors = {
  primary: '#0078D4',
  success: '#107C10',
  warning: '#FFB900',
  danger: '#D13438',
  purple: '#5C2D91',
  chart: ['#0078D4', '#40E0D0', '#107C10', '#FFB900', '#5C2D91', '#D13438'],
};

const classMetrics = {
  attendance: 38,
  enrolled: 42,
  duration: 92,
  completionRate: 90.5,
  avgQuizScore: 82,
  participationRate: 71,
};

const attendanceTimeline = Array.from({ length: 12 }, (_, i) => ({
  minute: `${i * 10}m`,
  present: Math.floor(38 - i * 0.5 + Math.random() * 2),
}));

const quizResults = [
  { question: 'Q1: Funnel basics', correct: 36, incorrect: 6 },
  { question: 'Q2: Metrics calculation', correct: 32, incorrect: 10 },
  { question: 'Q3: Tools comparison', correct: 28, incorrect: 14 },
  { question: 'Q4: Best practices', correct: 34, incorrect: 8 },
  { question: 'Q5: Case study', correct: 30, incorrect: 12 },
];

const participationDistribution = [
  { range: '0-20%', students: 5 },
  { range: '21-40%', students: 8 },
  { range: '41-60%', students: 12 },
  { range: '61-80%', students: 11 },
  { range: '81-100%', students: 6 },
];

const topicEngagement = [
  { topic: 'Funnels', understanding: 85, interest: 92, questions: 12 },
  { topic: 'Journey Mapping', understanding: 78, interest: 88, questions: 8 },
  { topic: 'Analytics', understanding: 72, interest: 85, questions: 15 },
  { topic: 'Tools', understanding: 68, interest: 90, questions: 18 },
  { topic: 'Case Studies', understanding: 80, interest: 95, questions: 6 },
];

const breakoutRoomMetrics = [
  { room: 'Room 1', participants: 8, engagement: 88, completed: true },
  { room: 'Room 2', participants: 9, engagement: 92, completed: true },
  { room: 'Room 3', participants: 7, engagement: 75, completed: true },
  { room: 'Room 4', participants: 8, engagement: 82, completed: false },
  { room: 'Room 5', participants: 6, engagement: 95, completed: true },
];

const performanceRadar = [
  { metric: 'Attendance', value: 90, fullMark: 100 },
  { metric: 'Participation', value: 71, fullMark: 100 },
  { metric: 'Quiz Score', value: 82, fullMark: 100 },
  { metric: 'Engagement', value: 76, fullMark: 100 },
  { metric: 'Question Quality', value: 85, fullMark: 100 },
  { metric: 'Completion', value: 90, fullMark: 100 },
];

// Program structure for dendrogram
const programStructure = {
  name: 'Marketing Analytics 101',
  children: [
    {
      name: 'Module 1: Fundamentals',
      children: [
        { name: 'Class 1: Intro' },
        { name: 'Class 2: Metrics' },
        { name: 'Class 3: Tools' },
      ]
    },
    {
      name: 'Module 2: Advanced',
      children: [
        { name: 'Class 4: Funnels' },
        { name: 'Class 5: Attribution' },
        { name: 'Class 6: Cohorts' },
      ]
    },
    {
      name: 'Module 3: Practice',
      children: [
        { name: 'Class 7: Case Study A' },
        { name: 'Class 8: Case Study B' },
        { name: 'Class 9: Workshop' },
      ]
    },
  ]
};

// Breakout rooms structure
const breakoutStructure = {
  name: 'Session 12',
  children: [
    {
      name: 'Room 1',
      children: [
        { name: 'Ana M.' },
        { name: 'Carlos R.' },
        { name: 'María L.' },
        { name: 'Pedro S.' },
        { name: 'Laura F.' },
        { name: 'Juan D.' },
        { name: 'Sofia T.' },
        { name: 'Diego P.' },
      ]
    },
    {
      name: 'Room 2',
      children: [
        { name: 'Elena G.' },
        { name: 'Roberto M.' },
        { name: 'Patricia N.' },
        { name: 'Andrés V.' },
        { name: 'Carmen S.' },
        { name: 'Luis B.' },
        { name: 'Valeria K.' },
        { name: 'Martín H.' },
        { name: 'Isabel R.' },
      ]
    },
    {
      name: 'Room 3',
      children: [
        { name: 'Fernando C.' },
        { name: 'Gabriela L.' },
        { name: 'Rodrigo F.' },
        { name: 'Daniela M.' },
        { name: 'Sebastián O.' },
        { name: 'Lucía P.' },
        { name: 'Tomás A.' },
      ]
    },
    {
      name: 'Room 4',
      children: [
        { name: 'Carolina W.' },
        { name: 'Javier Q.' },
        { name: 'Natalia E.' },
        { name: 'Pablo R.' },
        { name: 'Romina S.' },
        { name: 'Nicolás G.' },
        { name: 'Florencia M.' },
        { name: 'Maximiliano T.' },
      ]
    },
    {
      name: 'Room 5',
      children: [
        { name: 'Alejandra J.' },
        { name: 'Gustavo N.' },
        { name: 'Verónica P.' },
        { name: 'Matías L.' },
        { name: 'Cecilia F.' },
        { name: 'Ignacio B.' },
      ]
    },
  ]
};

const ClassAnalytics = () => {
  const [viewMode, setViewMode] = useState('metrics');
  return (
    <Box sx={{ p: 3, minHeight: '100vh', background: '#f5f7fa' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          Class Analytics - Deep Dive
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Chip label="Marketing Analytics 101" color="primary" size="small" />
          <Chip label="Session 12" variant="outlined" size="small" />
          <Chip label="Oct 11, 2025 • 10:00-11:32 AM" variant="outlined" size="small" />
          <Chip label="✓ Completed" sx={{ bgcolor: colors.success, color: 'white' }} size="small" />
        </Box>
      </Box>

      {/* Summary Cards with Gauges */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ textAlign: 'center', height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle2" sx={{ color: '#666', mb: 1, textTransform: 'uppercase', fontSize: '0.75rem' }}>
                Completion Rate
              </Typography>
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie
                    data={[
                      { name: 'Completed', value: classMetrics.completionRate, fill: colors.success },
                      { name: 'Remaining', value: 100 - classMetrics.completionRate, fill: alpha(colors.success, 0.1) },
                    ]}
                    cx="50%"
                    cy="50%"
                    startAngle={180}
                    endAngle={0}
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={0}
                    dataKey="value"
                  >
                    <Cell key="completed" />
                    <Cell key="remaining" />
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <Typography variant="h3" sx={{ fontWeight: 800, color: colors.success, mt: -8 }}>
                {classMetrics.completionRate}%
              </Typography>
              <Typography variant="caption" sx={{ color: '#666' }}>
                Students completed
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ textAlign: 'center', height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle2" sx={{ color: '#666', mb: 1, textTransform: 'uppercase', fontSize: '0.75rem' }}>
                Engagement Gauge
              </Typography>
              <ResponsiveContainer width="100%" height={180}>
                <RadialBarChart
                  innerRadius="60%"
                  outerRadius="90%"
                  data={[{ name: 'Participation', value: classMetrics.participationRate, fill: colors.primary }]}
                  startAngle={180}
                  endAngle={0}
                >
                  <RadialBar
                    minAngle={15}
                    background={{ fill: alpha(colors.primary, 0.1) }}
                    clockWise
                    dataKey="value"
                    cornerRadius={10}
                  />
                </RadialBarChart>
              </ResponsiveContainer>
              <Typography variant="h3" sx={{ fontWeight: 800, color: colors.primary, mt: -8 }}>
                {classMetrics.participationRate}%
              </Typography>
              <Typography variant="caption" sx={{ color: '#666' }}>
                Active participation
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ textAlign: 'center', height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle2" sx={{ color: '#666', mb: 1, textTransform: 'uppercase', fontSize: '0.75rem' }}>
                Quiz Performance
              </Typography>
              <Box sx={{ position: 'relative', width: 160, height: 180, mx: 'auto' }}>
                <svg width={160} height={180} viewBox="0 0 160 180">
                  <defs>
                    <clipPath id="liquid-clip">
                      <circle cx="80" cy="90" r="70" />
                    </clipPath>
                    <linearGradient id="liquid-gradient" x1="0%" y1="100%" x2="0%" y2="0%">
                      <stop offset="0%" stopColor={colors.warning} stopOpacity={0.9} />
                      <stop offset="100%" stopColor={alpha(colors.warning, 0.6)} />
                    </linearGradient>
                  </defs>
                  
                  {/* Outer circle */}
                  <circle
                    cx="80"
                    cy="90"
                    r="70"
                    fill="none"
                    stroke={alpha(colors.warning, 0.2)}
                    strokeWidth="3"
                  />
                  
                  {/* Liquid fill with wave */}
                  <g clipPath="url(#liquid-clip)">
                    <path
                      d={`
                        M 10 ${180 - (classMetrics.avgQuizScore * 1.6)}
                        Q 30 ${180 - (classMetrics.avgQuizScore * 1.6) - 8}, 50 ${180 - (classMetrics.avgQuizScore * 1.6)}
                        T 90 ${180 - (classMetrics.avgQuizScore * 1.6)}
                        T 130 ${180 - (classMetrics.avgQuizScore * 1.6)}
                        T 170 ${180 - (classMetrics.avgQuizScore * 1.6)}
                        L 170 180
                        L 10 180
                        Z
                      `}
                      fill="url(#liquid-gradient)"
                    />
                  </g>
                  
                  {/* Text */}
                  <text
                    x="80"
                    y="85"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontSize="32"
                    fontWeight="800"
                    fill="white"
                    style={{ textShadow: '0 2px 8px rgba(0,0,0,0.3)' }}
                  >
                    {classMetrics.avgQuizScore}%
                  </text>
                </svg>
              </Box>
              <Typography variant="caption" sx={{ color: '#666', display: 'block', mt: 1 }}>
                Average quiz score
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ textAlign: 'center' }}>
            <CardContent>
              <SchoolIcon sx={{ fontSize: 32, color: colors.primary, mb: 1 }} />
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {classMetrics.attendance}/{classMetrics.enrolled}
              </Typography>
              <Typography variant="caption" sx={{ color: '#666' }}>
                Attendance
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ textAlign: 'center' }}>
            <CardContent>
              <DurationIcon sx={{ fontSize: 32, color: colors.success, mb: 1 }} />
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {classMetrics.duration}m
              </Typography>
              <Typography variant="caption" sx={{ color: '#666' }}>
                Duration
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ textAlign: 'center' }}>
            <CardContent>
              <GroupsIcon sx={{ fontSize: 32, color: colors.danger, mb: 1 }} />
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                5
              </Typography>
              <Typography variant="caption" sx={{ color: '#666' }}>
                Breakout Rooms
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts Row 1 */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} lg={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Attendance Over Time
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={attendanceTimeline}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="minute" />
                <YAxis domain={[0, 42]} />
                <Tooltip />
                <Area
                  type="monotone"
                  dataKey="present"
                  stroke={colors.primary}
                  fill={alpha(colors.primary, 0.3)}
                  name="Present Students"
                />
              </AreaChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Quiz Performance
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={quizResults}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="question" tick={{ fontSize: 10 }} angle={-15} textAnchor="end" height={70} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="correct" fill={colors.success} name="Correct" radius={[4, 4, 0, 0]} />
                <Bar dataKey="incorrect" fill={colors.danger} name="Incorrect" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Charts Row 2 */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} lg={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Participation Distribution
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={participationDistribution} layout="horizontal">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis type="category" dataKey="range" width={80} />
                <Tooltip />
                <Bar dataKey="students" fill={colors.primary} name="Students" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Topic Performance Radar (Visx)
            </Typography>
            <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 2 }}>
              Understanding vs Interest across topics
            </Typography>
            <svg width={550} height={300}>
              <LinearGradient id="radar-gradient" from={colors.primary} to={colors.success} />
              <Group top={150} left={275}>
                {(() => {
                  const data = topicEngagement;
                  const numLevels = 5;
                  const radius = 120;
                  const angleSlice = (Math.PI * 2) / data.length;
                  
                  const radialScale = scaleLinear({
                    domain: [0, 100],
                    range: [0, radius],
                  });

                  // Grid circles
                  return (
                    <>
                      {/* Grid circles */}
                      {Array.from({ length: numLevels }, (_, i) => (
                        <circle
                          key={`grid-${i}`}
                          r={radialScale((i + 1) * 20)}
                          fill="none"
                          stroke={alpha(colors.primary, 0.15)}
                          strokeWidth={1}
                        />
                      ))}
                      
                      {/* Axis lines */}
                      {data.map((d, i) => {
                        const angle = angleSlice * i - Math.PI / 2;
                        const x = Math.cos(angle) * radius;
                        const y = Math.sin(angle) * radius;
                        return (
                          <g key={`axis-${i}`}>
                            <line
                              x1={0}
                              y1={0}
                              x2={x}
                              y2={y}
                              stroke={alpha(colors.primary, 0.2)}
                              strokeWidth={1}
                            />
                            <text
                              x={Math.cos(angle) * (radius + 25)}
                              y={Math.sin(angle) * (radius + 25)}
                              fontSize={10}
                              fontWeight="600"
                              textAnchor="middle"
                              dominantBaseline="middle"
                              fill="#333"
                            >
                              {d.topic}
                            </text>
                          </g>
                        );
                      })}

                      {/* Understanding polygon */}
                      <polygon
                        points={data.map((d, i) => {
                          const angle = angleSlice * i - Math.PI / 2;
                          const r = radialScale(d.understanding);
                          return `${Math.cos(angle) * r},${Math.sin(angle) * r}`;
                        }).join(' ')}
                        fill={alpha(colors.primary, 0.3)}
                        stroke={colors.primary}
                        strokeWidth={2}
                      />

                      {/* Interest polygon */}
                      <polygon
                        points={data.map((d, i) => {
                          const angle = angleSlice * i - Math.PI / 2;
                          const r = radialScale(d.interest);
                          return `${Math.cos(angle) * r},${Math.sin(angle) * r}`;
                        }).join(' ')}
                        fill={alpha(colors.success, 0.2)}
                        stroke={colors.success}
                        strokeWidth={2}
                        strokeDasharray="4 4"
                      />

                      {/* Data points */}
                      {data.map((d, i) => {
                        const angle = angleSlice * i - Math.PI / 2;
                        const r1 = radialScale(d.understanding);
                        const r2 = radialScale(d.interest);
                        return (
                          <g key={`point-${i}`}>
                            <circle
                              cx={Math.cos(angle) * r1}
                              cy={Math.sin(angle) * r1}
                              r={4}
                              fill={colors.primary}
                              stroke="white"
                              strokeWidth={2}
                            />
                            <circle
                              cx={Math.cos(angle) * r2}
                              cy={Math.sin(angle) * r2}
                              r={4}
                              fill={colors.success}
                              stroke="white"
                              strokeWidth={2}
                            />
                          </g>
                        );
                      })}

                      {/* Legend */}
                      <g transform={`translate(${-radius - 20}, ${radius + 30})`}>
                        <circle r={5} fill={colors.primary} />
                        <text x={10} y={5} fontSize={11} fill="#666">Understanding</text>
                        <circle cx={100} r={5} fill={colors.success} />
                        <text x={110} y={5} fontSize={11} fill="#666">Interest</text>
                      </g>
                    </>
                  );
                })()}
              </Group>
            </svg>
          </Paper>
        </Grid>
      </Grid>

      {/* Dendrograms */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} lg={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Program Structure (Dendrogram)
            </Typography>
            <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 2 }}>
              Course modules and class hierarchy
            </Typography>
            <svg width={550} height={350}>
              <LinearGradient id="program-gradient" from={colors.primary} to={colors.purple} />
              <Cluster
                root={hierarchy(programStructure)}
                size={[320, 520]}
                separation={(a, b) => (a.parent === b.parent ? 1 : 2) / a.depth}
              >
                {(cluster) => (
                  <Group top={20} left={20}>
                    {cluster.links().map((link, i) => (
                      <LinkVertical
                        key={`program-link-${i}`}
                        data={link}
                        stroke="url(#program-gradient)"
                        strokeWidth="2"
                        fill="none"
                        opacity={0.6}
                      />
                    ))}
                    {cluster.descendants().map((node, i) => {
                      const isRoot = node.depth === 0;
                      const isModule = node.depth === 1;
                      const isClass = node.depth === 2;
                      
                      return (
                        <Group key={`program-node-${i}`} top={node.x} left={node.y}>
                          <circle
                            r={isRoot ? 12 : isModule ? 8 : 6}
                            fill={isRoot ? colors.primary : isModule ? colors.success : colors.warning}
                            stroke="white"
                            strokeWidth={2}
                          />
                          <text
                            dy="0.33em"
                            dx={12}
                            fontSize={isRoot ? 13 : isModule ? 11 : 10}
                            fontWeight={isRoot ? 700 : isModule ? 600 : 500}
                            fill={isRoot ? colors.primary : isModule ? colors.success : '#666'}
                            style={{ pointerEvents: 'none' }}
                          >
                            {node.data.name}
                          </text>
                        </Group>
                      );
                    })}
                  </Group>
                )}
              </Cluster>
            </svg>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Breakout Rooms Distribution
            </Typography>
            <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 2 }}>
              Student assignment across 5 breakout rooms
            </Typography>
            <svg width={550} height={350}>
              <LinearGradient id="breakout-gradient" from={colors.secondary} to={colors.success} />
              <Cluster
                root={hierarchy(breakoutStructure)}
                size={[300, 520]}
                separation={(a, b) => (a.parent === b.parent ? 0.8 : 1.5) / a.depth}
              >
                {(cluster) => (
                  <Group top={20} left={20}>
                    {cluster.links().map((link, i) => (
                      <LinkVertical
                        key={`breakout-link-${i}`}
                        data={link}
                        stroke="url(#breakout-gradient)"
                        strokeWidth="1.5"
                        fill="none"
                        opacity={0.5}
                      />
                    ))}
                    {cluster.descendants().map((node, i) => {
                      const isSession = node.depth === 0;
                      const isRoom = node.depth === 1;
                      const isStudent = node.depth === 2;
                      
                      return (
                        <Group key={`breakout-node-${i}`} top={node.x} left={node.y}>
                          <circle
                            r={isSession ? 10 : isRoom ? 7 : 4}
                            fill={isSession ? colors.purple : isRoom ? colors.secondary : colors.success}
                            stroke="white"
                            strokeWidth={isSession ? 2 : isRoom ? 1.5 : 1}
                            opacity={0.9}
                          />
                          {!isStudent && (
                            <text
                              dy="0.33em"
                              dx={10}
                              fontSize={isSession ? 12 : 10}
                              fontWeight={isSession ? 700 : 600}
                              fill={isSession ? colors.purple : colors.secondary}
                              style={{ pointerEvents: 'none' }}
                            >
                              {node.data.name}
                            </text>
                          )}
                        </Group>
                      );
                    })}
                  </Group>
                )}
              </Cluster>
            </svg>
          </Paper>
        </Grid>
      </Grid>

      {/* Breakout Rooms Table */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Breakout Room Analytics
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Room</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Participants</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Engagement</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Task Completed</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {breakoutRoomMetrics.map((room) => (
                <TableRow key={room.room}>
                  <TableCell>{room.room}</TableCell>
                  <TableCell align="center">{room.participants}</TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={room.engagement}
                        sx={{
                          flex: 1,
                          height: 8,
                          borderRadius: 4,
                          bgcolor: alpha(colors.primary, 0.1),
                          '& .MuiLinearProgress-bar': {
                            bgcolor: room.engagement >= 80 ? colors.success : room.engagement >= 60 ? colors.warning : colors.danger,
                          }
                        }}
                      />
                      <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 40 }}>
                        {room.engagement}%
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={room.completed ? 'Yes' : 'No'}
                      size="small"
                      sx={{
                        bgcolor: room.completed ? alpha(colors.success, 0.15) : alpha(colors.danger, 0.15),
                        color: room.completed ? colors.success : colors.danger,
                        fontWeight: 600,
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default ClassAnalytics;

